import { http } from "@/lib/api";
const loadImage = (blob) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = URL.createObjectURL(blob);
  });

// Crops fully transparent margins only — subject pixels stay untouched.
// Bounding box of visible pixels, computed on the small mask then scaled to the full canvas.
function trimTransparent(c, mask) {
  const m = document.createElement("canvas");
  m.width = mask.naturalWidth;
  m.height = mask.naturalHeight;
  const mctx = m.getContext("2d");
  mctx.drawImage(mask, 0, 0);
  const { data, width, height } = mctx.getImageData(0, 0, m.width, m.height);
  m.width = 0;
  m.height = 0;
  let minX = width, minY = height, maxX = -1, maxY = -1;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      if (data[(y * width + x) * 4 + 3] > 8) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return c;
  const kx = c.width / width;
  const ky = c.height / height;
  const x0 = Math.max(0, Math.floor((minX - 1) * kx));
  const y0 = Math.max(0, Math.floor((minY - 1) * ky));
  const x1 = Math.min(c.width, Math.ceil((maxX + 2) * kx));
  const y1 = Math.min(c.height, Math.ceil((maxY + 2) * ky));
  const out = document.createElement("canvas");
  out.width = x1 - x0;
  out.height = y1 - y0;
  out.getContext("2d").drawImage(c, x0, y0, out.width, out.height, 0, 0, out.width, out.height);
  return out;
}

function toGrayscale(src) {
  const c = document.createElement("canvas");
  c.width = src.width;
  c.height = src.height;
  const ctx = c.getContext("2d");
  ctx.drawImage(src, 0, 0);
  const id = ctx.getImageData(0, 0, c.width, c.height);
  const d = id.data;
  for (let i = 0; i < d.length; i += 4) {
    const l = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
    d[i] = d[i + 1] = d[i + 2] = l;
  }
  ctx.putImageData(id, 0, 0);
  return c;
}

const IOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
const MAX_SIDE = IOS ? 1600 : 2000;
const free = (c) => { if (c) { c.width = 0; c.height = 0; } };
const MASK_SIDE = 1024;
const scaled = (img, max) => {
  const k = Math.min(1, max / Math.max(img.naturalWidth || img.width, img.naturalHeight || img.height));
  const c = document.createElement("canvas");
  c.width = Math.round((img.naturalWidth || img.width) * k);
  c.height = Math.round((img.naturalHeight || img.height) * k);
  c.getContext("2d").drawImage(img, 0, 0, c.width, c.height);
  return c;
};
const toBlob = (c, type = "image/png", q) => new Promise((r) => c.toBlob(r, type, q));

let worker = null;
let idleTimer = null;
// Frees the AI model memory (important on iPad/iPhone) once no cutout is running.
const scheduleIdle = () => {
  clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    if (worker && jobs.size === 0) { worker.terminate(); worker = null; }
  }, 3000);
};
let seq = 0;
const jobs = new Map();
const getWorker = () => {
  if (worker) return worker;
  worker = new Worker(new URL("./cutout.worker.js", import.meta.url));
  worker.onmessage = ({ data }) => {
    const job = jobs.get(data.id);
    if (!job) return;
    if (data.type === "progress") return job.onProgress?.(data.type, data.value);
    jobs.delete(data.id);
    scheduleIdle();
    data.type === "done" ? job.resolve(data.blob) : job.reject(new Error(data.message));
  };
  worker.onerror = () => {
    jobs.forEach((j) => j.reject(new Error("WORKER")));
    jobs.clear();
    worker.terminate();
    worker = null;
  };
  return worker;
};


const localCutout = (blob, onProgress) => new Promise((resolve, reject) => {
  const id = ++seq;
  clearTimeout(idleTimer);
  jobs.set(id, { resolve, reject, onProgress });
  getWorker().postMessage({ id, blob });
});

// remove.bg (server) first; free in-browser model only as a fallback.
async function cutoutMask(blob, onProgress) {
  try {
    const fd = new FormData();
    fd.append("image_file", blob, "photo.jpg");
    onProgress?.("remote", 0.3);
    const { data } = await http.post("/cutout", fd, { responseType: "blob", timeout: 30000 });
    onProgress?.("remote", 1);
    return data;
  } catch {
    return localCutout(blob, onProgress);
  }
}

export async function processPhoto(file, onProgress) {
  let img;
  try {
    img = await loadImage(file);
  } catch {
    throw new Error("FORMAT");
  }
  const full = scaled(img, MAX_SIDE);
  const smallCanvas = scaled(img, MASK_SIDE);
  const small = await toBlob(smallCanvas, "image/jpeg", 0.95);
  free(smallCanvas);
  URL.revokeObjectURL(img.src);
  img.src = "";
  img = null;
  const maskImg = await loadImage(await cutoutMask(small, onProgress));
  // Original pixels at full resolution; only the alpha mask is upscaled.
  const ctx = full.getContext("2d");
  ctx.globalCompositeOperation = "destination-in";
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(maskImg, 0, 0, full.width, full.height);
  const color = trimTransparent(full, maskImg);
  if (color !== full) free(full);
  URL.revokeObjectURL(maskImg.src);
  const cutoutBlob = await toBlob(color);
  return withLazyBw({ original: file, color, cutoutBlob, previewUrl: URL.createObjectURL(cutoutBlob) });
}

// The black & white canvas is only created on first access (B&W mode), halving canvas memory on iPad.
const bwCache = new WeakMap();
const withLazyBw = (p) => Object.defineProperty(p, "bw", {
  get() {
    if (!bwCache.has(p)) bwCache.set(p, toGrayscale(p.color));
    return bwCache.get(p);
  },
});

export const releasePhoto = (p) => {
  if (!p) return;
  free(p.color);
  free(bwCache.get(p));
  bwCache.delete(p);
  if (p.previewUrl) URL.revokeObjectURL(p.previewUrl);
};

export async function restorePhoto({ original, cutoutBlob }) {
  const img = await loadImage(cutoutBlob);
  const color = scaled(img, MAX_SIDE);
  URL.revokeObjectURL(img.src);
  return withLazyBw({ original, color, cutoutBlob, previewUrl: URL.createObjectURL(cutoutBlob) });
}
